import type { Tagged } from "@-/com.npmjs.type-fest";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import {
  useVoiceStudyLifecycle,
  type VoiceStudySessionHandle,
} from "@-/dev.improve.haniverse.lib.voice-study";

/**
 * Proof that the Voice pill should show: a pending start, an active session (its handle, stable for
 * the session's lifetime), or a failure to report. Changes only on lifecycle transitions.
 */
export type VoiceStudyControlsGate = Tagged<
  | { readonly kind: "starting" }
  | { readonly kind: "active"; readonly session: VoiceStudySessionHandle }
  | { readonly error: string; readonly kind: "failed" },
  "VoiceStudyControlsGate"
>;

/**
 * `null` = the designed hidden state: no session (idle). A pending permission request shows the
 * toolbar immediately, with a way to cancel. The caller re-renders only on lifecycle transitions
 * — never per message — because the gate is the lifecycle value itself.
 */
export function useVoiceStudyControlsGate(): VoiceStudyControlsGate | null {
  const lifecycle = useVoiceStudyLifecycle();
  switch (lifecycle.kind) {
    case "starting":
    case "active":
    case "failed": {
      return createGate<VoiceStudyControlsGate>(lifecycle);
    }
    case "idle": {
      return null;
    }
  }
}
